import { eventCallbacks, getCurrentRowElement, getRequiredBrowseData, handlePopupBrowseTabPress, performCodeEELSearch, PerformCodeEELSearchData } from "#skyward_browse";
import { addBrowse, getBrowseById, getInnerBrowse, prependOpenBrowse, removeBrowse, removeOpenBrowse } from "#skyward_browseList";
import { Binding, ModalLayer, showCodeEELDialog, BeforeCloseFunction, CloseReason, CloseReasons } from "#skyward_modalLayer";
import { closePopup } from "#skyward_window";
import { AjaxErrorLog } from "./skyward.base.ajaxError";
import { ensureLiveAreaAnnounced, getIdGenerator, SequentialOperationHandler } from "./skyward.base.extensions";
import { isAndroidApp, mobileAccess, virtualDir } from "./skyward.base.global";
import { DependencySnapshot, getDependencyDataWithoutWaiting, getRequestDataHashes, HashTransaction, isNotEmpty, TextBoxData, waitForDependencies, setupDependency } from "./skyward.base.requestDataHash";
import { focusAfter } from "./skyward.base.tabbing";
import { getScopeContainer, getValue, setValue } from "./skyward.ui.controls";
import debounce from "./skyward.ui.debounce";
import { setFocus } from "./skyward.ui.dialogFocusManager";
import { setFocusToTextBoxOnClick } from "./skyward.ui.eel";
import { registerEvents } from "./skyward.ui.events";
import { Browse, CodeEELData } from "./types";

const idGenerator = getIdGenerator("popup", "_codeEelBrowse");

interface AjaxData
{
    code: string;
    description: string;
    selectedValueDisplayData: string;
    additionalFields?: Record<string, string>;
    selectedValueHash: string | undefined;
    deniedAdditionalFields?: Record<string, string>;
}

type EelValueData =
{
    eelData: EelData;
    dynamicFilterHashes?: Record<string, string | null>;
    codeRouteHash: string;
    descriptionRouteHash: string;
    codeQueryParameterDataHash: string;
    descriptionQueryParameterDataHash: string;
    requestDataHashSrc: string;
    codeRequestDataHash?: string;
    descriptionRequestDataHash?: string;
    rowDataNonce: string;
    selectedValueHash: string;
} & Record<string, string | number>;

const events =
{
    init
};

registerEvents("codeEEL", events);

function init(selector: HTMLElement)
{
    const element = $(selector);
    if (!element.hasClass("eventsBound"))
    {
        registerCodeEEL(element);
    }
}

class EelData
{
    codeEEL: JQuery<HTMLElement>;
    codeEELCode: JQuery<HTMLElement>;
    codeEELCodeButton: JQuery<HTMLElement>;
    codeEELDescription: JQuery<HTMLElement>;
    codeEELSelectedValueDisplayField: JQuery<HTMLElement>;
    codeEELDescriptionButton: JQuery<HTMLElement>;
    codeEELValue: JQuery<HTMLInputElement>;
    filter: Record<string, string>;
    postData: Record<string, string>;
    tempTableName: string;
    additionalFieldsControls: Record<string, string>;
    hidePrimaryBrowseButton: boolean;
    hideOptionsButton: boolean;
    hideBrowseControl: boolean;
    layer: ModalLayer | null;
    codeEELValueData: EelValueData;
    additionalDynamicFilterValues?: Record<string, string | null>;

    lastValue: string | null; // stores the last searched value to prevent unnecessary searches
    selectedValue: string | null = null;
    singleRequestHandler: SequentialOperationHandler;
    transaction: HashTransaction;
    get isInited()
    {
        return Boolean(this.codeEELValueData.eelData);
    }

    constructor(codeEEL: JQuery<HTMLElement>)
    {
        this.codeEEL = codeEEL;
        const codeEELCode = codeEEL.find(".codeEELCode");
        if (codeEELCode.length > 0)
        {
            codeEELCode.data().lastValidatedValue = codeEELCode.val();
        }
        this.codeEELCode = codeEELCode;
        this.codeEELCodeButton = codeEEL.find(".codeEELCodeButton");
        const codeEELDescription = codeEEL.find(".codeEELDescription");
        if (codeEELDescription.length > 0)
        {
            codeEELDescription.data().lastValidatedValue = codeEELDescription.val();
        }
        this.codeEELDescription = codeEELDescription;
        this.codeEELSelectedValueDisplayField = codeEEL.find(".codeEELSelectedValueDisplayField");
        this.codeEELDescriptionButton = codeEEL.find(".codeEELDescriptionButton");
        this.codeEELValue = codeEEL.find(".codeEELValue") as JQuery<HTMLInputElement>;
        this.filter = codeEEL.data("filter");
        this.postData = codeEEL.data("postdata");
        this.tempTableName = codeEEL.data("tempTableName");
        this.additionalFieldsControls = codeEEL.data("additional-fields-controls");
        this.hidePrimaryBrowseButton = codeEEL.data("hidePrimaryBrowseButton");
        this.hideOptionsButton = codeEEL.data("hideOptionsButton");
        this.hideBrowseControl = codeEEL.data("hideBrowseControl");
        this.layer = null;
        this.additionalDynamicFilterValues = codeEEL.data("additional-dynamic-filter-values");
        this.lastValue = null;
        this.singleRequestHandler = new SequentialOperationHandler();
        this.transaction = new HashTransaction(this.codeEELValue);

        this.codeEELValueData = this.codeEELValue.data() as EelValueData;

        if (this.codeEELValueData.eelData)
        {
            return this.codeEELValueData.eelData;
        }
    }

    init()
    {
        if (this.isInited)
        {
            return this;
        }

        const label = this.codeEEL.findLabel();
        if (label.length)
        {
            const labelCopy = label.clone();
            labelCopy.remove(".requiredAsterisk");
            const labelHiddenTextElement = labelCopy.find(".sr-only");
            let labelText = "";
            if (labelHiddenTextElement.length > 0)
            {
                labelText = `${labelHiddenTextElement.text()} `;
                labelHiddenTextElement.remove();
            }

            labelText += labelCopy.text().replace(/\*/g, "");
            this.codeEELCode.attr("aria-label", labelText + " code");
            this.codeEELDescription.attr("aria-label", labelText + " description");
            // The forID of codeEELs points to the container.
            // Assign the forID to the first textbox in the container for valid accessibility.
            const firstInputId = (this.codeEELCode.length ? this.codeEELCode : this.codeEELDescription).attr("id");
            if (firstInputId)
            {
                label.attr("for", firstInputId);
            }
        }

        this.codeEELValueData.eelData = this;
        loadRequestDataHashes(this.codeEELValueData);
        setFocusToTextBoxOnClick(this.codeEEL);

        this.codeEEL.on("click", ".codeEELButton:not(.ui-disabled):not(.mobile-eel-button)", this.bindEventListener((element) =>
        {
            // Set the selected id to the hidden value on load in case an id is already selected
            this.selectedValue = this.codeEELValue.val() as string;

            if (this.layer === undefined || this.layer === null || !this.layer.onscreen)
            {
                const codeEELTextbox = $(element).siblings("input.eELInputContainer__textBox");
                this.showPopup(codeEELTextbox);
                this.doSearch();
            }
            else if (!mobileAccess)
            {
                this.layer.close(CloseReasons.SelectedItem);
            }
        }));

        this.codeEEL.on("keydown", "input:not([readonly],.ui-disabled)", this.bindEventListener(this.keydownEvent));

        this.codeEEL.off("refresh").on("refresh", this.bindEventListener(this.refreshEvent));
        setupDependency(this.codeEELCode.data() as TextBoxData, { Id: this.codeEEL.attr("id")!, Refresh: "true" }, getScopeContainer(this.codeEEL));
        setupDependency(this.codeEELDescription.data() as TextBoxData, { Id: this.codeEEL.attr("id")!, Refresh: "true" }, getScopeContainer(this.codeEEL));

        this.addInitialInputEvent();

        this.codeEEL.addClass("eventsBound");
        this.codeEELValue.trigger("controlLoaded");
        return this;
    }

    getBrowsePostData()
    {
        return {
            filter: JSON.stringify(this.filter),
            ...this.postData
        };
    }

    closeOpenEelBrowse(browseId: string, textbox: JQuery<HTMLElement>)
    {
        this.layer = null;
        this.lastValue = null;

        this.singleRequestHandler.close();

        if (!mobileAccess)
        {
            textbox.trigger("focus");
        }

        this.addInitialInputEvent();

        removeBrowse(browseId);

        removeOpenBrowse(browseId);

        textbox.trigger("CodeEelClosed");
    }

    handleTabPress(browseId: string, textbox: JQuery<HTMLElement>)
    {
        if (this.codeEEL.is(".quickEntryText"))
        {
            closePopup(undefined, CloseReasons.Tab);
            this.closeOpenEelBrowse(browseId, textbox);
            focusAfter(textbox);
        }
        else
        {
            handlePopupBrowseTabPress();
        }
    }

    bindEventListener<TEvent>(listener: (element: HTMLElement, event: TEvent) => any)
    {
        const self = this;
        return function (this: HTMLElement, event: TEvent)
        {
            return listener.call(self, this, event);
        };
    }

    createModalLayer(browseId: string, textbox: JQuery<HTMLElement>)
    {
        const textBoxId = textbox.attr("id");
        const popupContent = $("<div>", { class: "popup--browse", id: textBoxId + "_popup" });
        const browse = $("<div>", { id: browseId, class: "browse browseStaticHeight" });
        popupContent.append(browse);

        const announcementRegion = $("<div>", { class: "popup--announcement sr-only", "aria-live": "polite", role: "status" });
        popupContent.append(announcementRegion);

        const textBoxContainer = textbox.closest(".js-showEELContainer");

        const beforeClose: BeforeCloseFunction = (_layer, reason) =>
        {
            let triggerChange = false;
            let updateBoxes = false;
            // If hidden value is not the same as the selected id then update
            if (this.selectedValue != this.codeEELValue.val())
            {
                const browse = getBrowseById(browseId)!;
                const selectedRow = browse.primaryKeys!.indexOf(parseInt(this.selectedValue!, 10));
                this.codeEELValueData.selectedValueHash = selectedRow > -1 ? browse.rowDataHash![selectedRow] : null!;
                this.codeEELValue.val(this.selectedValue!);
                triggerChange = true;
            }

            if (shouldUpdateBoxesOnClose(this.codeEEL, this.selectedValue, reason))
            {
                updateBoxes = true;
                const request = this.updateBoxes(false);
                if (triggerChange)
                {
                    request.then(() =>
                    {
                        this.codeEELValue.trigger("change");
                    });
                }
            }

            if (triggerChange && !updateBoxes)
            {
                this.codeEELValue.trigger("change");
            }

            eventCallbacks.recordSelectionMade.deregisterCallback(selectionMadeHandler);

            this.closeOpenEelBrowse(browseId, textbox);

            textbox.attr("aria-controls", null);
            textbox.attr("aria-activedescendant", null);
            textbox.attr("aria-expanded", "false");
        };

        const selectionMadeHandler = (eventData?: Record<string, unknown>) =>
        {
            if (eventData?.browseId === browseId)
            {
                this.selectedValue = eventData.selectedId as string;
            }
        };

        eventCallbacks.recordSelectionMade.registerCallback(selectionMadeHandler);

        textbox.attr("aria-expanded", "true");

        const bindings: Binding[] =
            [
                {
                    onEvents: "keydown",
                    bindTo: browse,
                    runFunction: this.bindEventListener((_element, event) =>
                    {
                        if (textbox.length > 0 && event.currentTarget !== textbox[0]
                            && event.altKey && (event.key === "ArrowDown" || event.key === "ArrowUp"))
                        {
                            textbox.trigger("focus");
                            event.preventDefault();
                            event.stopPropagation();
                            return;
                        }

                        if (event.key === "Esc")
                        {
                            closePopup(undefined, CloseReasons.Escape);
                            this.closeOpenEelBrowse(browseId, textbox);
                        }
                    })
                },
                {
                    onEvents: "keydown",
                    bindTo: textbox,
                    runFunction: this.bindEventListener((element, event) =>
                    {
                        if (event.key === "Tab")
                        {
                            this.handleTabPress(browseId, $(element));
                            event.preventDefault();
                        }
                    })
                },
                {
                    // The Android app will trigger the input event whenever the value changes (even from code), we can't have this so we use the keyup event instead
                    onEvents: (isAndroidApp ? "keyup" : "input"),
                    bindTo: textbox,
                    runFunction: debounce(this.bindEventListener(this.inputEvent))
                },
                {
                    onEvents: `rowChanged browseLoaded.${browseId}`,
                    bindTo: browse,
                    runFunction: this.bindEventListener((_element, _event) =>
                    {
                        const browse = getBrowseById(browseId)!;
                        const row = getCurrentRowElement("unlocked", browseId, browse.currentRow!);
                        textbox.attr("aria-activedescendant", row.addIds().attr("id")!);
                        row.attr("aria-selected", "true");
                    })
                },
                {
                    onEvents: `browseLoaded.${browseId}`,
                    bindTo: browse,
                    runFunction: this.bindEventListener((_element, _event) =>
                    {
                        const browse = getBrowseById(browseId)!;

                        const listbox = this.layer?.container.find(".unlockedBrowseTableBody[role='listbox']");
                        if (listbox && listbox.length > 0)
                        {
                            listbox.addIds();
                            textbox.attr("aria-controls", listbox.attr("id")!);
                        }

                        const announcement = this.layer?.container.find(".popup--announcement");
                        // If there are no records, announce that.
                        if (announcement && browse.primaryKeys?.length === 0)
                        {
                            ensureLiveAreaAnnounced(announcement, "No records to display");
                        }
                        else
                        {
                            announcement?.text("");
                        }

                        const currentValue = this.codeEELValue.val()!;
                        if (!currentValue || currentValue === "")
                        {
                            return;
                        }

                        const rowUnlocked = getCurrentRowElement("unlocked", browseId, browse.currentRow!);
                        const rowLocked = getCurrentRowElement("locked", browseId, browse.currentRow!);
                        rowUnlocked.attr("aria-checked", "true");
                        rowUnlocked.addClass("codeEEL__browseRow--selected");
                        rowLocked.addClass("codeEEL__browseRow--selected");
                    })
                }
            ];

        this.layer = showCodeEELDialog(popupContent, textbox, textBoxContainer, beforeClose, this as unknown as CodeEELData, bindings);
    }

    getEELBrowseData(browseId: string, textbox: JQuery, dependencies: DependencySnapshot): Browse
    {
        const isCode = textbox.hasClass("codeEELCode");
        const pageScreen = textbox.data("action");
        return {
            id: browseId,
            pageModule: this.codeEELValueData.area,
            pageObject: this.codeEELValueData.controller,
            displayName: this.codeEELValueData.displayName,
            pageScreen: pageScreen,
            browseType: "Default",
            routeModule: this.codeEELValueData.area,
            routeObject: this.codeEELValueData.controller,
            dataModule: this.codeEELValueData.area, //TODO: PR 4339258 update to use actual dataModule instead of area
            dataObject: this.codeEELValueData.controller, //TODO: PR 4339258 update to use actual dataObject instead of controller
            browseName: "browse",
            selectMode: true,
            codeEelName: this.codeEELValue.attr("name"),
            codeEelDescription: this.codeEELDescription.attr("id"),
            hidePrimaryBrowseButton: this.hidePrimaryBrowseButton,
            routeHash: isCode ? this.codeEELValueData.codeRouteHash : this.codeEELValueData.descriptionRouteHash,
            requestDataHash: isCode ? this.codeEELValueData.codeRequestDataHash! : this.codeEELValueData.descriptionRequestDataHash,
            rowDataDefinitions:
            {
                value: this.codeEELValueData.primaryKeyName ?? ""
            },
            dynamicFilterHashes: this.codeEELValueData.dynamicFilterHashes as unknown,
            dynamicFilterValues: $.extend({}, this.additionalDynamicFilterValues, dependencies),
            rowDataNonce: this.codeEELValueData.rowDataNonce,
            taskName: "",
            hideBrowseControl: this.hideBrowseControl,
            refreshTimeout: -1,
            data: this.getBrowsePostData(),
            keyEventsEnabled: true,
            renderViewButton: true,
            disableBrowseSort: true,
            hideOptionsButton: this.hideOptionsButton,
            preventColumnResize: false,
            renderQuickFilter: false,
            queryParameterData: this.codeEEL.data("codeEelQueryParameterData"),
            queryParameterDataHash: isCode ? this.codeEELValueData.codeQueryParameterDataHash : this.codeEELValueData.descriptionQueryParameterDataHash,
            primaryKeys: [],
            resize() { },
        } as Browse;
    }

    addToBrowseList(browseId: string, textbox: JQuery<HTMLElement>)
    {
        // TODO (6756543) wait for dependencies before opening the popup
        const dependencies = getDependencyDataWithoutWaiting(textbox.data() as TextBoxData, getScopeContainer(textbox));
        addBrowse(this.getEELBrowseData(browseId, textbox, dependencies), browseId);
        prependOpenBrowse(browseId);
    }

    showPopup(textbox: JQuery<HTMLElement>)
    {
        const browseId = idGenerator.getNewId();
        this.addToBrowseList(browseId, textbox);
        this.createModalLayer(browseId, textbox);
    }

    doSearch(searchText?: string)
    {
        const browse = getInnerBrowse(this.layer!.container);
        if (!browse)
        {
            return;
        }

        const data: PerformCodeEELSearchData =
        {
            codeEELSearchText: searchText!,
            seekId: Number(this.codeEELValue.val()),
            tempTableName: this.tempTableName,
        };

        this.singleRequestHandler.run((signal) =>
        {
            return performCodeEELSearch(browse.id, data, signal);
        });
    }

    async updateBoxes(fireChangeEvent: boolean)
    {
        const textbox = this.getFirstTextBox();
        if (!textbox)
        {
            console.warn("Tried to set value on codeeel with no inputs");
            return;
        }
        fireChangeEvent = fireChangeEvent != false ? true : false;

        // this is infallible unless the object has been deleted between opening the code EEL and choosing a record
        const data = (await this.getBoxValues(this.codeEELValue.val(), { hideGlass: true }))!;

        populateBoxes(this.codeEEL, data, fireChangeEvent);

        if (data.additionalFields)
        {
            this.codeEEL.data("additionalFields", data.additionalFields);
        }

        if (data.deniedAdditionalFields)
        {
            this.codeEEL.data("deniedFields", data.deniedAdditionalFields);
        }

        if (!this.layer || this.layer.closing)
        {
            this.codeEEL.trigger("eelComplete");
        }

        updateAdditionalFieldsData(this.codeEEL, this.additionalFieldsControls, data.additionalFields!);
    }

    keydownEvent(element: HTMLElement, event: JQuery.KeyDownEvent)
    {
        const textbox = $(element);

        if (this.layer != null)
        {
            if (event.altKey && (event.key === "ArrowUp" || event.key === "ArrowDown"))
            {
                event.preventDefault();
                event.stopPropagation();

                setFocus(this.layer);
            }
            return;
        }

        const currentValue = getValue(this.codeEEL);
        if (event.key === "Escape" && currentValue && this.layer == null)
        {
            event.stopPropagation();
            clear(this.codeEEL, true);
            this.selectedValue = null;
            return;
        }

        if (event.key === "ArrowUp" || event.key === "ArrowDown" || event.key === "Enter") // Up arrow, down arrow and enter perform a search
        {
            if (mobileAccess && textbox.is(":animated"))
            {
                return;
            }

            this.showPopup(textbox);
            this.doSearch();
            this.selectedValue = null;
            return;
        }
    }


    refreshEvent(_element: HTMLElement, _event: JQuery.EventBase)
    {
        const value = this.codeEELValue.val();
        if (value !== undefined && value !== "")
        {
            void this.setValue(value, false, true, true);
        }
        return false;
    }

    inputEvent(element: HTMLElement)
    {
        const textbox = $(element);
        const textboxValue = textbox.val() as string;

        if (mobileAccess && textbox.is(":animated"))
        {
            return;
        }

        if (this.lastValue != null && textboxValue.toLowerCase() === this.lastValue.toLowerCase())
        {
            return;
        }

        if (this.layer == null)
        {
            this.showPopup(textbox);
        }

        this.doSearch(textboxValue);
        this.lastValue = textbox.val() as string;
        this.selectedValue = null;
    }

    addInitialInputEvent()
    {
        // This event is only added to be fired once, so the debounced event attached through the modal layer can take over
        // This event gets re-attached when the modal layer closes
        // The Android app will trigger the input event whenever the value changes (even from code), we can't have this so we use the keyup event instead
        this.codeEEL.one((isAndroidApp ? "keyup" : "input"), "input:not([readonly],.ui-disabled)", this.bindEventListener(this.inputEvent));
    }

    getFirstTextBox()
    {
        if (this.codeEELValueData.codeRequestDataHash && this.codeEELCode.length)
        {
            return this.codeEELCode;
        }
        else if (this.codeEELValueData.descriptionRequestDataHash && this.codeEELDescription.length)
        {
            return this.codeEELDescription;
        }
        return null;
    }

    copyTo(dest: EelData, suppressChangeEvent = false, updateCodeAndDescription = true)
    {
        dest.codeEELValue.val(this.codeEELValue.val()!);

        const data: AjaxData =
        {
            code: this.codeEELCode.val() as string,
            description: this.codeEELDescription.val() as string,
            selectedValueDisplayData: this.codeEEL.find(".codeEELSelectedValueDisplayField").val() as string,
            selectedValueHash: this.codeEELValueData.selectedValueHash
        };
        if (updateCodeAndDescription)
        {
            populateBoxes(dest.codeEEL, data, !suppressChangeEvent);
        }
        dest.codeEELValueData.selectedValueHash = data.selectedValueHash!;
    }

    async setValue(value: any, suppressChangeEvent = false, updateCodeAndDescription = true, forceUpdate = false)
    {
        let isSameValue = (value?.toString() ?? "") === (this.codeEELValue.val() ?? "");
        if (isSameValue && !forceUpdate)
        {
            return;
        }

        const textbox = this.getFirstTextBox();
        if (!textbox)
        {
            console.warn("Tried to set value on codeeel with no inputs");
            return;
        }

        return this.singleRequestHandler.run((signal) => this.transaction.run(signal, async (txn) =>
        {
            txn.setValue(value);

            let data: AjaxData | undefined;
            if (value !== null && value !== undefined && isNaN(value))
            {
                value = "";
            }

            if (isNotEmpty(value))
            {
                data = await this.getBoxValues(value, { signal, useOriginalValueHash: !forceUpdate });
                if (signal.aborted) { return; }
                if (data === undefined)
                {
                    txn.setValue("");
                    isSameValue = false;
                }
            }

            if (data === undefined)
            {
                data =
                {
                    code: "",
                    description: "",
                    selectedValueDisplayData: "",
                    selectedValueHash: undefined,
                };
            }

            if (updateCodeAndDescription)
            {
                populateBoxes(this.codeEEL, data, !suppressChangeEvent && !isSameValue);
            }
            this.codeEELValueData.selectedValueHash = data.selectedValueHash!;
        }));
    }

    async getBoxValues(id: string | number | undefined, options?: { hideGlass?: boolean, signal?: AbortSignal, useOriginalValueHash?: boolean }): Promise<AjaxData | undefined>
    {
        const textbox = this.getFirstTextBox()!;
        const dependencies = await waitForDependencies(textbox.data() as TextBoxData, getScopeContainer(textbox), options?.signal);
        const browse = this.getEELBrowseData("temp", textbox, dependencies);
        const postData = getRequiredBrowseData(browse, { tempTableName: this.tempTableName });

        if (id == this.codeEELValueData.originalValue && (options?.useOriginalValueHash ?? true))
        {
            postData.selectedValueHash = this.codeEELValueData.originalSelectedValueHash;
        }

        const ajax: JQuery.jqXHR<AjaxData | AjaxErrorLog> = $.ajax(
        {
            url: `${virtualDir}${this.codeEELValueData.area}/${this.codeEELValueData.controller}/codeeel/${id}`,
            type: "POST",
            dataType: "JSON",
            data: postData,
            activityIndicator: { hideGlass: options?.hideGlass },
            suppressErrors: (errorAjax) => isObjectNotFoundException(errorAjax),
            signal: options?.signal,
        });
        return ajax.catch((thrownAjax) =>
        {
            if (isObjectNotFoundException(thrownAjax))
            {
                return undefined;
            }
            else
            {
                throw thrownAjax;
            }
        });

        function isObjectNotFoundException(ajax: JQuery.jqXHR<AjaxData | AjaxErrorLog>)
        {
            // GUID for object not found exceptions, which are expected if the filter has changed
            return ajax.status === 500 && ajax.responseJSON?.clientIdentifier === "9C3EDFF2-8532-4428-9FA9-493B64285989";
        }
    }
}

export function registerCodeEEL(codeEEL: JQuery<HTMLElement>)
{
    getOrInitCodeEEL(codeEEL);
}

/**
 * Called to initialize a code eel that has been cloned from an original. Ensures it has a separate copy of internal data structures.
 */
export function reinitializeCodeEEL(codeEEL: JQuery<HTMLElement>)
{
    // Save selected value hash
    const hiddenInput = codeEEL.find(".codeEELValue");
    const selectedValueHash = hiddenInput.data("selectedValueHash");

    // Remove old data
    codeEEL.find("*").addBack().removeData().off();
    registerCodeEEL(codeEEL);

    // registerCodeEEL preserved .val() but reset selectedValueHash to its initial value from requestDataHashSrc. Restore it.
    hiddenInput.data("selectedValueHash", selectedValueHash);

}

export function shouldUpdateBoxesOnClose(codeEEL: JQuery<HTMLElement>, selectedValue: string | null, reason?: CloseReason)
{
    const hasSelectedValue = selectedValue !== null && selectedValue !== "";
    if (!hasSelectedValue && codeEEL.data("allow-freeform-text") === true)
    {
        return false;
    }

    const updateForSelection = reason === CloseReasons.SelectedItem && (hasSelectedValue || codeEEL.data("keep-invalid-values") !== true);
    return updateForSelection || hasUnvalidatedText(codeEEL.find(".codeEELCode")) || hasUnvalidatedText(codeEEL.find(".codeEELDescription"));

    function hasUnvalidatedText(textbox: JQuery<HTMLElement>)
    {
        return textbox.length > 0 && textbox.val() !== textbox.data().lastValidatedValue;
    }
}

export function populateBoxes(codeEEL: JQuery<HTMLElement>, ajaxData: AjaxData, fireChangeEvent?: boolean)
{
    const codeEELCode = codeEEL.find(".codeEELCode");
    const codeEELCodeValue = ajaxData.code || "";
    codeEELCode.val(codeEELCodeValue);
    if (codeEELCode.length > 0)
    {
        codeEELCode.data().lastValidatedValue = codeEELCodeValue;
    }
    const codeEELDescription = codeEEL.find(".codeEELDescription");
    const codeEELDescriptionValue = ajaxData.description || "";
    codeEELDescription.val(codeEELDescriptionValue);
    if (codeEELDescription.length > 0)
    {
        codeEELDescription.data().lastValidatedValue = codeEELDescriptionValue;
    }
    codeEEL.find(".codeEELSelectedValueDisplayField").val(ajaxData.selectedValueDisplayData || "");
    codeEEL.trigger("codeEELValuesUpdated");
    codeEEL.find(".codeEELValue").data("selectedValueHash", ajaxData.selectedValueHash!);
    if (fireChangeEvent)
    {
        fireChangeEvents(codeEEL);
        updateAdditionalFieldsData(codeEEL, null, ajaxData.additionalFields!);
    }

    //Quick Entry Text needs to fire the dirty call again if a value was selected from the code EEL browse and not typed in.
    if (codeEEL.closest(".quickEntryText").length !== 0)
    {
        codeEEL.find(".codeEELDescription").trigger("dirty");
    }
}

export function clear(codeEEL: JQuery<HTMLElement>, fireChangeEvent?: boolean)
{
    codeEEL.find(".codeEELCode").val("");
    codeEEL.find(".codeEELDescription").val("");
    codeEEL.find(".codeEELSelectedValueDisplayField").val("");
    codeEEL.find(".codeEELValue").val("");
    codeEEL.trigger("codeEELValuesUpdated");
    if (fireChangeEvent)
    {
        fireChangeEvents(codeEEL);
    }
}

export function getQueryParameterData(codeEEL: JQuery<HTMLElement>)
{
    let queryParameterData = codeEEL.data("codeEelQueryParameterData");

    if (typeof queryParameterData === "string")
    {
        try
        {
            queryParameterData = JSON.parse(queryParameterData);
        }
        catch (e)
        {
            queryParameterData = {};
        }
    }

    return $.extend({}, queryParameterData);
}

export function setQueryParameterData(codeEEL: JQuery<HTMLElement>, queryParameterData: { [key: string]: string } | string)
{
    let newQueryParameterData = queryParameterData;

    if (typeof newQueryParameterData === "string" && newQueryParameterData !== "")
    {
        newQueryParameterData = JSON.parse(newQueryParameterData);
    }
    else if (typeof newQueryParameterData !== "object")
    {
        newQueryParameterData = "";
    }

    codeEEL.data("codeEelQueryParameterData", newQueryParameterData);
}

export function setCodeEELValue(codeEEL: JQuery<HTMLElement>, value: any, suppressChangeEvent = false, updateCodeAndDescription = true)
{
    const eelData = getOrInitCodeEEL(codeEEL);
    return eelData.setValue(value, suppressChangeEvent, updateCodeAndDescription);
}

export function copyValue(source: JQuery<HTMLElement>, dest: JQuery<HTMLElement>, suppressChangeEvent = false, updateCodeAndDescription = true)
{
    const sourceEel = getOrInitCodeEEL(source);
    const destEel = getOrInitCodeEEL(dest.closest(".codeEEL"));
    sourceEel.copyTo(destEel, suppressChangeEvent, updateCodeAndDescription);
}

function loadRequestDataHashes(codeEelData: EelValueData)
{
    if (codeEelData.requestDataHashReadonly)
    {
        return;
    }
    const id = codeEelData.requestDataHashKey;
    const data = getRequestDataHashes(codeEelData.requestDataHashSrc);
    if (!data)
    {
        return;
    }

    if (codeEelData.eelData.codeEELCode.length > 0)
    {
        codeEelData.codeRequestDataHash = data[id + "_code"]?.hash;
        codeEelData.dynamicFilterHashes = data[id + "_code"]?.dependencies;
    }

    if (codeEelData.eelData.codeEELDescription.length > 0)
    {
        codeEelData.descriptionRequestDataHash = data[id + "_description"]?.hash;
        if (!codeEelData.dynamicFilterHashes)
        {
            codeEelData.dynamicFilterHashes = data[id + "_description"]?.dependencies;
        }
    }

    codeEelData.rowDataNonce = data[id]?.hash ?? "";
    codeEelData.originalSelectedValueHash = codeEelData.selectedValueHash = data[id]?.initialValue ?? "";
}

function getOrInitCodeEEL(codeEEL: JQuery<HTMLElement>)
{
    return new EelData(codeEEL).init();
}

function fireChangeEvents(codeeel: JQuery<HTMLElement>)
{
    codeeel.find(".codeEELValue").trigger("change");
    if (codeeel.data("keep-invalid-values") === true)
    {
        codeeel.find(".codeEELCode").trigger("change");
        codeeel.find(".codeEELDescription").trigger("change");
    }
}

function updateAdditionalFieldsData(codeEEL: JQuery<HTMLElement>, codeEELAdditionalFieldsControls: { [key: string]: string } | null, additionalFields: { [key: string]: string } | null)
{
    if (codeEEL)
    {
        if (!codeEELAdditionalFieldsControls)
        {
            codeEELAdditionalFieldsControls = codeEEL.data("additional-fields-controls");
        }

        if (codeEELAdditionalFieldsControls)
        {
            const parentContainer = getScopeContainer(codeEEL);

            if (parentContainer.length > 0)
            {
                $.each(codeEELAdditionalFieldsControls, function (id, control)
                {
                    let value = null;
                    if (additionalFields)
                    {
                        const additionalFieldValue = additionalFields[id as string];
                        if (additionalFieldValue)
                        {
                            value = additionalFieldValue;
                        }
                    }

                    const nameElement = parentContainer.find("[name='" + JSON.parse(control) + "']");
                    setValue(nameElement, value);
                });
            }
        }
    }
}
